<template>
  <div>
    <component :is="contentHtmlTag" v-if="renderType === renderTypeEnum.Common" ref="contentRef">
      <div v-html="meta"></div>
    </component>
    <!-- currently only have ul, ol tags -->
    <component :is="contentHtmlTag" v-else-if="renderType === renderTypeEnum.WithChildren" ref="contentRef">
      <li v-for="(data, index) in list" :key="index" v-html="data" />
    </component>
    <component
      :is="contentHtmlTag"
      v-else-if="renderType === renderTypeEnum.Media"
      ref="contentRef"
      v-bind="mediaAttrs"
      @click="onMediaClick(mediaAttrs)"
    />
  </div>
</template>

<script>
import { useIntersectionObserver } from '@vueuse/core'
import { ProductDetailHtmlType } from '@/view-models/product'

export default defineComponent({
  name: 'ContentData',
  props: {
    type: { type: Number, default: ProductDetailHtmlType.Html },
    meta: { type: String, default: '' },
    list: { type: Array, default: () => [] },
    mediaWidth: { type: Number, default: 0 },
    mediaHeight: { type: Number, default: 0 },
  },
  emits: ['imageClick'],
  setup(props, ctx) {
    // data
    const contentRef = ref(null)
    const renderTypeEnum = Object.freeze({
      Common: 1,
      WithChildren: 2,
      Media: 3,
    })

    const { stop: stopObserveMedias } = useIntersectionObserver(
      contentRef,
      entries => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) {
            entry.target.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo' }), 'https://www.youtube.com')
          }
        })
      },
      {
        rootMargin: '0px',
        threshold: [0],
      }
    )

    // computed
    const contentHtmlTag = computed(() => {
      switch (props.type) {
        case ProductDetailHtmlType.Html:
          return 'div'
        case ProductDetailHtmlType.Image:
          return 'img'
        case ProductDetailHtmlType.Youtube:
          return 'iframe'
        case ProductDetailHtmlType.UnorderedList:
          return 'ul'
        case ProductDetailHtmlType.OrderedList:
          return 'ol'
        default:
          return 'div'
      }
    })

    const renderType = computed(() => {
      switch (props.type) {
        case ProductDetailHtmlType.Html:
          return renderTypeEnum.Common
        case ProductDetailHtmlType.Image:
        case ProductDetailHtmlType.Youtube:
          return renderTypeEnum.Media
        case ProductDetailHtmlType.OrderedList:
        case ProductDetailHtmlType.UnorderedList:
          return renderTypeEnum.WithChildren
        default:
          return renderTypeEnum.Common
      }
    })

    const mediaAttrs = computed(() => {
      const attrs = {
        src: '',
        class: '',
      }
      switch (props.type) {
        case ProductDetailHtmlType.Image:
          attrs.src = props.meta
          attrs.class = 'wd-w-full wd-cursor-pointer'
          attrs.style = {
            'aspect-ratio': Boolean(props.mediaHeight) && `${props.mediaWidth}/${props.mediaHeight}`,
          }
          break
        case ProductDetailHtmlType.Youtube:
          attrs.src = `https://www.youtube.com/embed/${props.meta}?enablejsapi=1`
          attrs.class = 'wd-w-full wd-aspect-video'
          attrs.allowfullscreen = true
          break
      }

      return attrs
    })

    // lifecycle
    onUnmounted(() => {
      stopObserveMedias()
    })

    function onMediaClick(mediaAttrs) {
      if (props.type === ProductDetailHtmlType.Image) {
        ctx.emit('imageClick', mediaAttrs.src)
      }
    }

    return {
      contentRef,
      contentHtmlTag,
      renderTypeEnum,
      renderType,
      mediaAttrs,
      onMediaClick,
    }
  },
})
</script>

<style></style>
